import { Box, Dialog, Typography } from "@mui/material";
import { ErrorBoundary } from "@sentry/react";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useMemo, useRef, useState } from "react";
import { libraryTemplateAtom } from "../discover/library/store";
import { brandToneAtom, queryAtom } from "../generate/store";
import { PromptSuggestions } from "../message/editor/plugins/co-pilot/co-pilot-prompt-suggestions";
import { RefineContentHistory } from "./ai-refine-content-history";
import { AiRefineInput } from "./ai-refine-input";
import { historyAtomFamily } from "./store";

import type { OmitUnion } from "../../utils/omit-union";
import type { ContentTone } from "../brand-kit/server/brand-kit-service";
import type { RefineHistory } from "../generic/atoms/types/refine";
import type { PromptSuggestion } from "../message/editor/plugins/co-pilot/co-pilot-prompt-suggestions";
import type { DialogProps } from "@mui/material";
import type { FC } from "react";

export const AiRefineContentModal: FC<
  {
    title: string;
    contentTone: ContentTone | "custom";
    config: OmitUnion<RefineHistory, "prompt">;
    onClose: () => void;
  } & DialogProps
> = ({ title, contentTone, config, onClose, ...popoverProps }) => {
  const historyContainer = useRef<HTMLDivElement>(null);

  const [scrollFade, setScrollFade] = useState(0);
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop } = e.currentTarget;
    setScrollFade(Math.min(scrollTop, 20));
  }, []);

  const handleChange = useCallback(() => {
    setTimeout(() => {
      historyContainer.current?.scrollTo?.({
        top: historyContainer.current.scrollHeight,
        behavior: "smooth",
      });
    }, 50);
  }, []);

  const promptSuggestions: PromptSuggestion[] = [
    {
      action: "lengthen",
      label: "Lengthen",
    },
    {
      action: "shorten",
      label: "Shorten",
    },
    {
      action: "regenerate",
      label: "Regenerate",
    },
  ];

  const query = useAtomValue(queryAtom);
  const template = useAtomValue(libraryTemplateAtom);
  const customTone = useAtomValue(brandToneAtom);
  const key = useMemo(
    () => ({
      contentKey: contentTone,
      ...(contentTone === "custom" && customTone && { customTone }),
      ...(config.store === "generate" ? { query } : { template }),
    }),
    [config.store, contentTone, customTone, query, template],
  );
  const [history, setHistory] = useAtom(historyAtomFamily(key));

  const handleUsePromptSuggestion = useCallback(
    (prompt: string) => {
      setHistory([...history, { ...config, prompt }]);
    },
    [setHistory, history, config],
  );

  return (
    <Dialog onClose={onClose} {...popoverProps}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: 2,
          maxHeight: "calc(80vh)",
          minWidth: 600,
        }}
      >
        <ErrorBoundary
          fallback={
            <Typography variant="body1">
              It appears CoPilot isn’t working right now. Try again later, or contact your customer
              services representative.
            </Typography>
          }
        >
          <Box sx={{ pt: 0.5, flex: "0 0 auto" }}>
            <Typography variant="h6">{title}</Typography>
          </Box>

          {history.length === 0 && (
            <PromptSuggestions
              onUsePrompt={handleUsePromptSuggestion}
              loading={false}
              suggestions={promptSuggestions}
            />
          )}
          <Box
            ref={historyContainer}
            onScroll={handleScroll}
            sx={[
              {
                flex: "1 1 auto",
                mb: 2,
                pr: 2,
              },
              (theme) => ({
                overflowY: "scroll",
                maskImage: `linear-gradient(to top, ${theme.palette.background.paper} calc(100% - ${scrollFade}px), transparent 100%)`,
              }),
            ]}
          >
            <RefineContentHistory
              tone={contentTone}
              onChange={handleChange}
              store={config.store}
              onUseSuggestion={onClose}
            />
          </Box>

          <Box sx={{ flex: "0 0 auto" }}>
            <AiRefineInput tone={contentTone} config={config} />
          </Box>
        </ErrorBoundary>
      </Box>
    </Dialog>
  );
};
